import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ProjectAreaNav } from './ProjectAreaNav';
import { ProjectHeaderMeta } from './ProjectHeaderMeta';

import { InlineTextCell } from '@/components/planning/cells';

/**
 * The project header, following the Project Portfolio drawer's hierarchy
 * (Web/src/pages/projects/portfolio/components/ProjectDetails/).
 *
 * The alignment this replaces was the reported problem. Previously the title sat
 * in a left column while a pill nav group floated against the header's right
 * edge, so the two shared no baseline and the save-status chip collided with the
 * nav. Now:
 *
 * - Title and project number share one baseline, the number as a parenthesised
 *   `label` immediately after the name — the design system's pattern. The title
 *   is click-to-edit wherever the name is ours to change (`onRename`).
 * - After the code: a static "Draft" status chip, and nothing else. The
 *   Completion Principle pill that sat here (ADR 0041) was removed on 30 Oct
 *   2026 (Mette: no longer needed); the principle itself still configures
 *   Financials from the budget-model flag. The autosave/sync chips live in the
 *   main panel's bottom-right corner (`Panels.Main`'s `footer`), not here.
 * - A second line, `Customer · PM · Project details ›` (`ProjectHeaderMeta`,
 *   docs/briefs/project-details-v2/brief.md): who the project is for and who
 *   runs it, and the way to the page that describes it. On every tab; only the
 *   link is flagged.
 * - Area navigation (`ProjectAreaNav`) sits on the title's row, right-aligned
 *   (ADR 0045). It lived in the sidebar for a while, but the left rail is
 *   TimeLog's product-level navigation — Register, Reports, Projects,
 *   Employees, Invoices, Customers — and per-project sub-navigation cannot
 *   live there. ADR 0031 gave it a row of its own because the save-status chip
 *   then owned the title's right edge; that chip has since moved to the main
 *   panel's footer, so the row was given back (2026-10-01, the pilot's ask).
 */

export type ProjectHeaderBarProps = {
  projectName: string;
  projectCode?: string;
  /**
   * Makes the title click-to-edit. Omit it where the page has no document to
   * write the name into, and the title stays plain text.
   */
  onRename?: (name: string) => void;
  /**
   * Reserved for the breadcrumb, on a row of its own above the title. Empty
   * today — TimeLog has no breadcrumb yet — and the row is not rendered at all
   * until something fills it.
   */
  leading?: ReactNode;
};

export const ProjectHeaderBar = ({
  projectName,
  projectCode,
  onRename,
  leading,
}: ProjectHeaderBarProps) => {
  const { t } = useTranslation('pmShell');
  const [editing, setEditing] = useState(false);

  /* `color="primary"` resolves to navy-100 in the design system. */
  const title = (
    <Typography
      variant="display2"
      component="h1"
      color="primary"
      data-automation-id={onRename ? undefined : 'project-name'}
    >
      {projectName}
    </Typography>
  );

  return (
    /*
      One row since 2026-10-01 (the pilot's ask, ADR 0045): the title on the
      left, level with the area pills on the right, so the page gives the space
      of a whole row back. `mb: -1` takes PanelContent's 16px section gap down
      to 8px under the header only — the rails keep their rhythm.
    */
    <Stack gap={1} data-automation-id="project-header" sx={{ mb: -1 }}>
      {leading ? <Box sx={{ minWidth: 0 }}>{leading}</Box> : null}
      <Stack
        direction="row"
        // Top-aligned: when the main panel is too narrow for the whole title
        // group (Planning with its drawer open), the group wraps under the
        // name, and the pills must stay level with the name, not centre on it.
        sx={{ alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: 'baseline',
            gap: 1,
            flexWrap: 'wrap',
            flex: 1,
            // A long single-token project name must not push the row wider
            // than the panel; at the 600px panel floor there is no slack.
            minWidth: 0,
            overflowWrap: 'anywhere',
          }}
        >
          {onRename ? (
            <InlineTextCell
              value={projectName}
              display={title}
              editing={editing}
              onStartEdit={() => setEditing(true)}
              onCommit={(next) => {
                setEditing(false);
                // An empty title keeps the old name: the plan rows can fall
                // back to 'Untitled', but blanking a project is never a thing
                // someone means to do.
                const trimmed = next.trim();
                if (trimmed !== '' && trimmed !== projectName) onRename(trimmed);
              }}
              onCancel={() => setEditing(false)}
              // The input inherits the h1's size and weight, so the line does
              // not jump between reading and editing.
              inputSx={{ fontSize: 20, fontWeight: 500, color: 'var(--color-navy-100)' }}
              inputWidth={360}
              underlineColor="var(--color-pink-80)"
              automationId="project-name"
            />
          ) : (
            title
          )}
          {projectCode ? (
            <Typography
              variant="label"
              component="p"
              sx={{ color: 'var(--fg-2)' }}
              data-automation-id="project-code"
            >
              ({projectCode})
            </Typography>
          ) : null}
          {/*
            Static (decision 1 of docs/briefs/project-details-v2/brief.md): every
            project reads "Draft", with no way to change it — the library's
            status chip (P7: outlined navy, mono). On every tab, not flagged.
          */}
          <Chip
            size="small"
            variant="outlined"
            color="navy"
            monoText
            label={t('header.Draft')}
            data-automation-id="project-status-chip"
            sx={{ alignSelf: 'center' }}
          />
        </Stack>
        <ProjectAreaNav />
      </Stack>
      <ProjectHeaderMeta />
      {/*
        The rule under the header, on every tab (Mette, 2 Nov): the project's
        identity above, its page below. A little extra room above it; below,
        the header's own -8px is taken back so the page starts 16px under it.
      */}
      <Divider
        data-automation-id="project-header-divider"
        sx={{ mt: 0.5, mb: 1, borderColor: 'var(--color-fog-40)' }}
      />
    </Stack>
  );
};
